import { productionPerHour, type Resource, RESOURCES } from "@zend/content";
import { HOUR } from "../time.js";

export interface ProducerState {
  resource: Resource;
  level: number;
}

export interface ProductionInput {
  producers: ProducerState[];
  stored: Record<Resource, number>;
  /** Storage capacity for RAT/POL/ALY/ENC; SCR is uncapped. */
  capacity: number;
  lastSettledAt: number;
  now: number;
  /** Additive production bonus in percent (research + VIP + protocols + morale). */
  bonusPct: number;
  powerDeficit: boolean;
  burning?: boolean;
  /** Integer numerators below PRODUCTION_CARRY_DENOMINATOR, persisted between settlements. */
  carry?: Partial<Record<Resource, number>>;
}

export interface ProductionResult {
  gained: Record<Resource, number>;
  stored: Record<Resource, number>;
  settledAt: number;
  carry: Record<Resource, number>;
}

export const POWER_DEFICIT_PRODUCTION_MULT = 0.75;
/** Basis points for bonuses, quarter power, half burning, and millisecond accrual. */
export const PRODUCTION_CARRY_DENOMINATOR = HOUR * 10_000 * 4 * 2;

function rateNumerators(producers: ProducerState[], bonusPct: number, powerDeficit: boolean, burning: boolean): Record<Resource, bigint> {
  const rates = Object.fromEntries(RESOURCES.map((r) => [r, 0n])) as Record<Resource, bigint>;
  const multiplier = BigInt(Math.max(0, Math.round((100 + bonusPct) * 100))) * (powerDeficit ? 3n : 4n) * (burning ? 1n : 2n);
  for (const producer of producers) rates[producer.resource] += BigInt(productionPerHour(producer.resource, producer.level)) * multiplier;
  return rates;
}

/** Lazily settles production for the elapsed time. Non-Scrip resources stop at capacity (overflow is lost). */
export function settleProduction(input: ProductionInput): ProductionResult {
  const gained = Object.fromEntries(RESOURCES.map((r) => [r, 0])) as Record<Resource, number>;
  const stored = { ...input.stored };
  const carry = Object.fromEntries(RESOURCES.map((r) => [r, input.carry?.[r] ?? 0])) as Record<Resource, number>;
  if (input.now <= input.lastSettledAt) return { gained, stored, carry, settledAt: input.lastSettledAt };
  const elapsed = BigInt(input.now - input.lastSettledAt);
  const denominator = BigInt(PRODUCTION_CARRY_DENOMINATOR);
  const rates = rateNumerators(input.producers, input.bonusPct, input.powerDeficit, input.burning ?? false);
  for (const r of RESOURCES) {
    const numerator = rates[r] * elapsed + BigInt(carry[r]);
    let add = Number(numerator / denominator);
    carry[r] = Number(numerator % denominator);
    if (r !== "SCR") {
      const room = Math.max(0, input.capacity - stored[r]);
      // Production stops at the cap: overflow, including its fraction, is not saved for a later spend.
      if (add >= room) { add = room; carry[r] = 0; }
    }
    gained[r] = add;
    stored[r] += add;
  }
  return { gained, stored, carry, settledAt: input.now };
}

/** Hourly rate summary for UI (already including bonuses). */
export function productionRates(producers: ProducerState[], bonusPct: number, powerDeficit: boolean, burning = false): Record<Resource, number> {
  const rates = rateNumerators(producers, bonusPct, powerDeficit, burning);
  return Object.fromEntries(RESOURCES.map((r) => [r, Math.round(Number(rates[r]) / (PRODUCTION_CARRY_DENOMINATOR / HOUR))])) as Record<Resource, number>;
}
